import {
  EngagementRuleEditorPage,
  ExperienceEditorPage,
  ExperienceGuidesPage,
  ExperiencesPage,
} from './lazy-pages';

import type { PageOrRedirectRouteEntry } from './route-table.types';

/**
 * The surfaces where an operator authors WHAT A VISITOR IS SHOWN on their own
 * site — engagement rules and experience units.
 *
 * Hoisted out of `route-table.in-shell.ts` at the ~300-line ceiling
 * (`.claude/rules/architecture.md`) — and hoisted as THIS group rather than at
 * whatever line the count happened to cross, because these already sat adjacent
 * in the table and are one topic. They share a collision governor, a funnel, and
 * the same recurring explanation for why each editor is a full page rather than
 * a dialog.
 *
 * **What is NOT here, and why.** `/engagement` itself, and the `/page-rules/*`
 * exception editor, stay in the main table: the merged `/engagement` page is the
 * home of the whole surface (`route-table.in-shell.ts` registers it, and the
 * three retired paths redirect INTO it), and the page-rules editor is one of its
 * tabs' escape hatch rather than a member of this group. Keeping the list page
 * and its own editor in one file is what makes the redirect story readable in
 * one place.
 *
 * A LEAF: it imports the lazy components and the entry type, and nothing
 * imports it but the table, so the edge is one-directional.
 */
export const onPageAuthoringRouteEntries: readonly PageOrRedirectRouteEntry[] = [
  // On-page experiences A1 — the engagement-rule editor. `/engagement` itself is
  // registered in the main table (it is the merged surface's home); this is its
  // editor, a full PAGE for the same reason `/page-rules/new` is: a rule carries
  // a trigger, an audience, a message, a schedule, display, limits, a reach and
  // an experiment, which do not fit in a dialog without the primary action
  // leaving the fold.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/engagement/new',
    component: EngagementRuleEditorPage,
  },
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/engagement/:ruleId',
    component: EngagementRuleEditorPage,
  },
  // On-page experiences Phase B — `/experiences`, the authoring front door for
  // experience units (banners · modals · popovers · hotspots · showcases ·
  // inline and panel cards · announcements · surveys · guide launchers).
  //
  // **Routed with NO sidebar row, deliberately, and this is a decision somebody
  // should revisit rather than a thing forgotten.** Two reasons, and the second
  // is the one that would bite:
  //
  //  1. SURFACES sits at the A17 ≤6 scannable cap (six visible rows; the
  //     seventh is Labs-gated), so a row here needs a regroup rather than an
  //     append — and regrouping the nav is not this program's call to make.
  //  2. `/experience` (SINGULAR — the adaptive-compositions chat surface)
  //     already holds a row. `Experience` and `Experiences` one above the other
  //     is a navigation hazard, not a naming nit: an operator would learn which
  //     is which by getting it wrong.
  //
  // Reachability without a nav row is therefore carried by a LINK: the
  // `/engagement` page's HEADER points here, because the two surfaces are one
  // job by the model's own account — a unit and an engagement rule compete for
  // the same visitor's attention, share one collision governor and one funnel —
  // so merging them under a single nav row is the likely end state, and a link
  // between them is the honest interim rather than a seventh row nobody sized.
  //
  // The command palette cannot stand in for it: it is built from `sidebarItems`
  // precisely so it can never offer a destination the sidebar does not. So that
  // one link is the whole of this route's reachability, and of the three routes
  // below it. It lives in the page HEADER rather than in a tab panel for that
  // reason — a door that only opens once you have found the right tab is not
  // reachability — and `EngagementPage.test.tsx` pins it there on a tab that is
  // not its own.
  { kind: 'page', placement: 'in-shell', path: '/experiences', component: ExperiencesPage },
  // `/new` BEFORE `/:unitKey`, because a unit key is `[A-Za-z0-9_-]{1,64}` and
  // `new` is a legal one — so the parameterised route would swallow it and the
  // create flow would render "we can't find that" for a unit nobody has made.
  // React Router v7 ranks static segments above dynamic ones regardless of
  // order, so this is belt rather than the only thing holding it; the ordering
  // is here so a reader does not have to know that to see it is handled.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/experiences/new',
    component: ExperienceEditorPage,
  },
  // Before `/:unitKey` for the same reason `/new` is — `guides` is a legal unit
  // key, so the parameterised route would otherwise swallow it and render "we
  // can't find that" for a page that exists.
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/experiences/guides',
    component: ExperienceGuidesPage,
  },
  {
    kind: 'page',
    placement: 'in-shell',
    path: '/experiences/:unitKey',
    component: ExperienceEditorPage,
  },
];
